import { useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, Clock, X } from 'lucide-react';
import { countActiveFilters, filterBrowseOptions } from '../utils/browseFilterParams';
import { cookwareLabel } from '../utils/cookwareLabels';
import useKeyboardInset from '../hooks/useKeyboardInset';

/**
 * Mobile-first multi-select filter panel presented as a bottom sheet.
 *
 * All four catalog categories (Tags, Cuisine, Protein / Meat Type, Cookware)
 * and Cook Time are multi-select. The whole body scrolls; Clear All / Apply
 * stay sticky at the bottom. Selecting one option NEVER hides the others.
 *
 * Props:
 *  - open, onOpenChange
 *  - options: { tags, cuisines, proteins, cookware, dishTypes }
 *  - value:   { tags, cuisines, proteins, cookware, cookTimes, dishTypes }
 *  - onApply(selection)
 */
function ChipGroup({ label, options, selected, onToggle, testPrefix, optionLabel }) {
  if (!options || options.length === 0) return null;
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
        {label}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map(function (opt) {
          var isSelected = selected.indexOf(opt) !== -1;
          return (
            <button
              key={opt}
              type="button"
              onClick={function () { onToggle(opt); }}
              className={'px-3 py-1.5 text-xs rounded-full border transition-all shrink-0 ' +
                (isSelected
                  ? 'bg-accent text-white border-accent'
                  : 'bg-secondary text-muted-foreground border-transparent hover:bg-secondary/80')}
              data-testid={testPrefix + '-' + opt}
            >
              {optionLabel ? optionLabel(opt) : opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FilterSheet({ open, onOpenChange, options, value, onApply }) {
  options = options || {};
  var [draft, setDraft] = useState({ tags: [], cuisines: [], proteins: [], cookware: [], cookTimes: [], dishTypes: [] });
  var [optionSearch, setOptionSearch] = useState('');
  // Lift the sheet above the iOS keyboard while "Search filters..." is typed in: narrowing the chips
  // shrinks the sheet, which would otherwise sink under the keyboard with Clear All / Apply.
  var kb = useKeyboardInset();

  // Re-seed the draft from the applied value each time the sheet opens.
  useEffect(function () {
    if (open) {
      setDraft({
        tags: (value && value.tags) ? value.tags.slice() : [],
        cuisines: (value && value.cuisines) ? value.cuisines.slice() : [],
        proteins: (value && value.proteins) ? value.proteins.slice() : [],
        cookware: (value && value.cookware) ? value.cookware.slice() : [],
        cookTimes: (value && value.cookTimes) ? value.cookTimes.slice() : [],
        dishTypes: (value && value.dishTypes) ? value.dishTypes.slice() : [],
      });
      setOptionSearch('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(category, opt) {
    setDraft(function (prev) {
      var next = Object.assign({}, prev);
      var arr = prev[category].slice();
      var idx = arr.indexOf(opt);
      if (idx === -1) arr.push(opt); else arr.splice(idx, 1);
      next[category] = arr;
      return next;
    });
  }

  var filtered = useMemo(function () {
    return filterBrowseOptions(options, optionSearch);
  }, [options, optionSearch]);

  var draftCount = countActiveFilters(draft);

  var cookTimeOptions = [15, 30, 45, 60];

  function clearAll() {
    setDraft({ tags: [], cuisines: [], proteins: [], cookware: [], cookTimes: [], dishTypes: [] });
  }

  function apply() {
    if (onApply) onApply(draft);
    if (onOpenChange) onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="p-0 rounded-t-2xl max-h-[85vh] flex flex-col"
        style={kb ? { bottom: kb, maxHeight: 'calc(100vh - ' + kb + 'px - env(safe-area-inset-top) - 0.5rem)' } : undefined}
        data-testid="filter-sheet"
      >
        {/* Header */}
        <div className="px-4 pt-5 pb-3 border-b border-border/40">
          <h2 className="font-heading font-bold text-lg mb-3">Filters</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
            <Input
              placeholder="Search filters..."
              value={optionSearch}
              onChange={function (e) { setOptionSearch(e.target.value); }}
              data-testid="filter-search-input"
              className="pl-9 h-9 bg-secondary/30 border-transparent focus:border-accent"
            />
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5" data-testid="filter-sheet-body">
          <ChipGroup
            label="Tags"
            options={filtered.tags}
            selected={draft.tags}
            onToggle={function (o) { toggle('tags', o); }}
            testPrefix="filter-tag"
          />
          <ChipGroup
            label="Cuisine"
            options={filtered.cuisines}
            selected={draft.cuisines}
            onToggle={function (o) { toggle('cuisines', o); }}
            testPrefix="filter-cuisine"
          />
          <ChipGroup
            label="Protein / Meat Type"
            options={filtered.proteins}
            selected={draft.proteins}
            onToggle={function (o) { toggle('proteins', o); }}
            testPrefix="filter-protein"
          />
          <ChipGroup
            label="Dish Type"
            options={filtered.dishTypes}
            selected={draft.dishTypes}
            onToggle={function (o) { toggle('dishTypes', o); }}
            testPrefix="filter-dish-type"
          />
          <ChipGroup
            label="Cookware"
            options={filtered.cookware}
            selected={draft.cookware}
            onToggle={function (o) { toggle('cookware', o); }}
            testPrefix="filter-cookware"
            optionLabel={cookwareLabel}
          />

          {/* Cook Time (buckets) */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
              Cook Time
            </label>
            <div className="flex flex-wrap gap-2">
              {cookTimeOptions.map(function (time) {
                var isSelected = draft.cookTimes.indexOf(time) !== -1;
                return (
                  <button
                    key={time}
                    type="button"
                    onClick={function () { toggle('cookTimes', time); }}
                    className={'px-3 py-1.5 text-xs rounded-full border transition-all flex items-center gap-1 ' +
                      (isSelected
                        ? 'bg-accent text-white border-accent'
                        : 'bg-secondary text-muted-foreground border-transparent hover:bg-secondary/80')}
                    data-testid={'filter-cooktime-' + time}
                  >
                    <Clock size={12} />
                    {time === 60 ? '60+ min' : time + ' min'}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="px-4 py-3 border-t border-border/40 flex gap-3 bg-background">
          <Button
            variant="outline"
            onClick={clearAll}
            className="flex-1 h-11 rounded-xl"
            data-testid="filter-clear-all-btn"
          >
            <X size={16} className="mr-1" />
            Clear All
          </Button>
          <Button
            onClick={apply}
            className="flex-1 h-11 rounded-xl bg-accent text-white hover:bg-accent/90"
            data-testid="filter-apply-btn"
          >
            Apply{draftCount > 0 ? ' (' + draftCount + ')' : ''}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default FilterSheet;
